import Background from '@/assets/video/background.jpg';
import { useHomeQuery } from '@/queries/home.query';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FaPlay, FaTimes } from 'react-icons/fa';

export default function YoutubeParallaxHero() {
  const { data, isLoading, isError } = useHomeQuery();

  const [open, setOpen] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);

  const youtubeId = data?.data?.video?.youtube_id ?? null;

  /* ========================
   * Preload background
   * ======================== */
  useEffect(() => {
    const img = new Image();
    img.src = Background;
    img.onload = () => setBgLoaded(true);
  }, []);

  /* ========================
   * Lock scroll on modal
   * ======================== */
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'auto';
    return () => (document.body.style.overflow = 'auto');
  }, [open]);

  if (isLoading || isError) {
    return null;
  }

  if (!youtubeId) return null;

  const thumbnail = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;

  return (
    <section
      className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden"
      style={{
        backgroundImage: bgLoaded ? `url(${Background})` : undefined,
        backgroundAttachment: 'fixed',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        transition: 'opacity 0.8s ease-out',
        opacity: bgLoaded ? 1 : 0,
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/60" />

      {/* Content */}
      <div className="relative z-10 text-center px-6 w-full max-w-screen-2xl">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-xs sm:text-sm font-medium tracking-[0.15em] uppercase text-gray-200"
        >
          Program & Portofolio Koperasi Syariah
        </motion.p>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-200 mt-2"
        >
          Mendukung kesejahteraan anggota dan masyarakat.
        </motion.h2>

        <VideoThumbnail thumbnail={thumbnail} onClick={() => setOpen(true)} />
      </div>

      {/* Modal */}
      <AnimatePresence>
        {open && <VideoModal youtubeId={youtubeId} onClose={() => setOpen(false)} />}
      </AnimatePresence>
    </section>
  );
}

/* ========================
 * Thumbnail
 * ======================== */
function VideoThumbnail({ thumbnail, onClick }) {
  return (
    <motion.div
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="relative cursor-pointer rounded-2xl overflow-hidden shadow-[0_25px_80px_rgba(0,0,0,0.55)]
                 bg-white/10 backdrop-blur-xl border border-white/20
                 mx-auto max-w-4xl mt-10 group"
    >
      <img
        src={thumbnail}
        alt="Thumbnail video"
        className="w-full h-[400px] md:h-[460px] object-cover
                   group-hover:scale-105 transition-transform duration-700"
        loading="lazy"
        decoding="async"
      />

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative bg-red-600 text-white p-6 rounded-full shadow-2xl">
          <FaPlay size={22} />
          <span className="absolute inset-0 animate-ping bg-red-600/30 rounded-full" />
        </div>
      </div>
    </motion.div>
  );
}

/* ========================
 * Modal
 * ======================== */
function VideoModal({ youtubeId, onClose }) {
  return (
    <>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-xl z-[99]"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div
        className="fixed inset-0 flex items-center justify-center z-[100] p-4"
        initial={{ opacity: 0, scale: 0.87 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.87 }}
      >
        <div className="relative w-full max-w-3xl bg-black rounded-xl overflow-hidden">
          <iframe
            className="w-full h-[230px] sm:h-[360px] md:h-[500px]"
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title="YouTube Video"
          />

          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-white/20 hover:bg-white/40
                       text-white p-3 rounded-full transition"
          >
            <FaTimes size={15} />
          </button>
        </div>
      </motion.div>
    </>
  );
}
